/**
 * Ready-to-use starter copy for each notification event + channel, in BOTH
 * app languages. The active set follows the business's language (i18n.language
 * mirrors the business `language` field via LanguageSync) — an English business
 * writes to its clients in English, a Brazilian one in Portuguese.
 *
 * Each event/channel offers one or more named variants (ex.: "Padrão"/"Default",
 * "Curto"/"Short") shown in the template picker modal. Tokens like {first_name},
 * {service}, {date} are substituted with the real appointment data at send time
 * (e-mail/SMS) and with sample data in the previews. Keyed by the event `key`
 * defined in notificationEvents.ts.
 */
export interface TemplateVariant {
    /** Short label shown on the template card (ex.: "Padrão" / "Default"). */
    name: string;
    /** The copy that fills the textarea when the variant is applied. */
    text: string;
}

export interface NotifTemplate {
    email?: TemplateVariant[];
    sms?: TemplateVariant[];
}

/** Normalizes an i18n language code ('en', 'en-US', 'pt-BR'…) to our two sets. */
export function templatesFor(lang?: string): Record<string, NotifTemplate> {
    return (lang ?? '').toLowerCase().startsWith('en')
        ? TEMPLATES_EN
        : TEMPLATES_PT;
}

const TEMPLATES_PT: Record<string, NotifTemplate> = {
    'new-booking': {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, recebemos seu pedido de agendamento de {service} para {date} às {time}. ' +
                    'Em breve confirmaremos o horário. Obrigado por escolher a {business}!',
            },
            {
                name: 'Curto',
                text: 'Olá {first_name}! Recebemos seu pedido de {service} para {date} às {time}. Já já confirmamos. — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: recebemos seu agendamento de {service} em {date} às {time}. Em breve confirmaremos.' },
            { name: 'Curto', text: '{business}: pedido de {service} recebido para {date} {time}. Aguarde a confirmação.' },
        ],
    },
    confirmation: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, seu agendamento de {service} está confirmado para {date} às {time}. ' +
                    'Esperamos você! — {business}',
            },
            {
                name: 'Curto',
                text: 'Confirmado, {first_name}! {service} em {date} às {time}. Até lá! — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: seu {service} está confirmado para {date} às {time}. Até lá!' },
            { name: 'Curto', text: '{business}: {service} confirmado, {date} {time}.' },
        ],
    },
    reschedule: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, seu agendamento de {service} foi remarcado para {date} às {time}. ' +
                    'Se precisar ajustar, é só responder este e-mail. — {business}',
            },
            {
                name: 'Curto',
                text: 'Olá {first_name}, seu {service} foi remarcado para {date} às {time}. — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: seu {service} foi remarcado para {date} às {time}.' },
        ],
    },
    reminder: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, passando para lembrar do seu {service} em {date} às {time}. ' +
                    'Nos vemos em breve! — {business}',
            },
            {
                name: 'Curto',
                text: 'Lembrete, {first_name}: {service} em {date} às {time}. Até logo! — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: 'Lembrete {business}: {service} em {date} às {time}. Até logo!' },
            { name: 'Curto', text: '{business}: lembrete do seu {service} em {date} {time}.' },
        ],
    },
    rebook: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, já faz um tempo desde sua última visita à {business}. ' +
                    'Que tal agendar um novo horário de {service}? Será um prazer atender você novamente.',
            },
            {
                name: 'Curto',
                text: 'Sentimos sua falta, {first_name}! Que tal remarcar seu {service} na {business}?',
            },
        ],
    },
    cancellation: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, seu agendamento de {service} em {date} às {time} foi cancelado. ' +
                    'Quando quiser, é só marcar um novo horário. — {business}',
            },
            {
                name: 'Curto',
                text: 'Olá {first_name}, seu {service} de {date} às {time} foi cancelado. — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: seu {service} de {date} às {time} foi cancelado.' },
        ],
    },
    decline: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, infelizmente não conseguiremos atender seu agendamento de {service} em {date} às {time}. ' +
                    'Pedimos desculpas e ficamos à disposição para encontrar um novo horário. — {business}',
            },
            {
                name: 'Curto',
                text: 'Olá {first_name}, não foi possível confirmar seu {service} de {date} às {time}. Vamos remarcar? — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: não foi possível confirmar seu {service} de {date} às {time}. Entre em contato para remarcar.' },
        ],
    },
    'waitlist-entry': {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, você entrou na lista de espera para {service} na {business}. ' +
                    'Avisaremos assim que um horário ficar disponível.',
            },
            {
                name: 'Curto',
                text: 'Olá {first_name}! Você está na lista de espera para {service}. Avisamos quando abrir vaga. — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: você está na lista de espera para {service}. Avisamos quando abrir vaga.' },
        ],
    },
    'waitlist-cancel': {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, você saiu da lista de espera para {service} na {business}. ' +
                    'Se quiser entrar novamente, é só nos avisar.',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: você saiu da lista de espera para {service}.' },
        ],
    },
    finish: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, obrigado por escolher a {business}! ' +
                    'Esperamos que tenha gostado do seu {service}. Até a próxima!',
            },
            {
                name: 'Curto',
                text: 'Obrigado pela visita, {first_name}! Esperamos ver você de novo em breve. — {business}',
            },
        ],
        sms: [
            { name: 'Padrão', text: '{business}: obrigado pela visita! Esperamos ver você novamente em breve.' },
        ],
    },
    'ask-review': {
        email: [
            {
                name: 'Padrão',
                text:
                    'Olá {first_name}, queremos saber como foi seu {service} na {business}. ' +
                    'Você poderia deixar uma avaliação? Sua opinião nos ajuda muito. Obrigado!',
            },
            {
                name: 'Curto',
                text: 'Oi {first_name}! Como foi seu {service} na {business}? Deixe sua avaliação 🙏',
            },
        ],
    },
    birthday: {
        email: [
            {
                name: 'Padrão',
                text:
                    'Feliz aniversário, {first_name}! 🎉 Toda a equipe da {business} deseja um dia incrível. ' +
                    'Passe para comemorar com a gente!',
            },
            {
                name: 'Curto',
                text: 'Feliz aniversário, {first_name}! 🎉 Um abraço de toda a equipe da {business}.',
            },
        ],
        sms: [
            { name: 'Padrão', text: 'Feliz aniversário, {first_name}! 🎉 — {business}' },
        ],
    },
};

const TEMPLATES_EN: Record<string, NotifTemplate> = {
    'new-booking': {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, we received your booking request for {service} on {date} at {time}. ' +
                    "We'll confirm your time shortly. Thank you for choosing {business}!",
            },
            {
                name: 'Short',
                text: "Hi {first_name}! We got your {service} request for {date} at {time}. We'll confirm soon. — {business}",
            },
        ],
        sms: [
            { name: 'Default', text: "{business}: we received your {service} booking for {date} at {time}. We'll confirm shortly." },
            { name: 'Short', text: '{business}: {service} request received for {date} {time}. Awaiting confirmation.' },
        ],
    },
    confirmation: {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, your {service} appointment is confirmed for {date} at {time}. ' +
                    'See you there! — {business}',
            },
            {
                name: 'Short',
                text: "You're confirmed, {first_name}! {service} on {date} at {time}. See you! — {business}",
            },
        ],
        sms: [
            { name: 'Default', text: '{business}: your {service} is confirmed for {date} at {time}. See you!' },
            { name: 'Short', text: '{business}: {service} confirmed, {date} {time}.' },
        ],
    },
    reschedule: {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, your {service} appointment was rescheduled to {date} at {time}. ' +
                    'Need to adjust? Just reply to this email. — {business}',
            },
            {
                name: 'Short',
                text: 'Hi {first_name}, your {service} was rescheduled to {date} at {time}. — {business}',
            },
        ],
        sms: [
            { name: 'Default', text: '{business}: your {service} was rescheduled to {date} at {time}.' },
        ],
    },
    reminder: {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, just a reminder of your {service} on {date} at {time}. ' +
                    'See you soon! — {business}',
            },
            {
                name: 'Short',
                text: 'Reminder, {first_name}: {service} on {date} at {time}. See you soon! — {business}',
            },
        ],
        sms: [
            { name: 'Default', text: '{business} reminder: {service} on {date} at {time}. See you soon!' },
            { name: 'Short', text: '{business}: reminder of your {service} on {date} {time}.' },
        ],
    },
    rebook: {
        email: [
            {
                name: 'Default',
                text:
                    "Hi {first_name}, it's been a while since your last visit to {business}. " +
                    'How about booking a new {service}? It would be a pleasure to see you again.',
            },
            {
                name: 'Short',
                text: 'We miss you, {first_name}! How about booking your next {service} at {business}?',
            },
        ],
    },
    cancellation: {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, your {service} appointment on {date} at {time} was cancelled. ' +
                    'Whenever you like, just book a new time. — {business}',
            },
            {
                name: 'Short',
                text: 'Hi {first_name}, your {service} on {date} at {time} was cancelled. — {business}',
            },
        ],
        sms: [
            { name: 'Default', text: '{business}: your {service} on {date} at {time} was cancelled.' },
        ],
    },
    decline: {
        email: [
            {
                name: 'Default',
                text:
                    "Hi {first_name}, unfortunately we won't be able to take your {service} appointment on {date} at {time}. " +
                    "We're sorry — we'd be glad to find you a new time. — {business}",
            },
            {
                name: 'Short',
                text: "Hi {first_name}, we couldn't confirm your {service} on {date} at {time}. Shall we rebook? — {business}",
            },
        ],
        sms: [
            { name: 'Default', text: "{business}: we couldn't confirm your {service} on {date} at {time}. Contact us to rebook." },
        ],
    },
    'waitlist-entry': {
        email: [
            {
                name: 'Default',
                text:
                    "Hi {first_name}, you've joined the waitlist for {service} at {business}. " +
                    "We'll let you know as soon as a time opens up.",
            },
            {
                name: 'Short',
                text: "Hi {first_name}! You're on the waitlist for {service}. We'll ping you when a spot opens. — {business}",
            },
        ],
        sms: [
            { name: 'Default', text: "{business}: you're on the waitlist for {service}. We'll let you know when a spot opens." },
        ],
    },
    'waitlist-cancel': {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, you left the waitlist for {service} at {business}. ' +
                    'Want back in? Just let us know.',
            },
        ],
        sms: [
            { name: 'Default', text: '{business}: you left the waitlist for {service}.' },
        ],
    },
    finish: {
        email: [
            {
                name: 'Default',
                text:
                    'Hi {first_name}, thank you for choosing {business}! ' +
                    'We hope you enjoyed your {service}. See you next time!',
            },
            {
                name: 'Short',
                text: 'Thanks for coming in, {first_name}! Hope to see you again soon. — {business}',
            },
        ],
        sms: [
            { name: 'Default', text: '{business}: thanks for your visit! We hope to see you again soon.' },
        ],
    },
    'ask-review': {
        email: [
            {
                name: 'Default',
                text:
                    "Hi {first_name}, we'd love to hear how your {service} at {business} went. " +
                    'Could you leave us a review? Your feedback helps a lot. Thank you!',
            },
            {
                name: 'Short',
                text: 'Hi {first_name}! How was your {service} at {business}? Leave us a review 🙏',
            },
        ],
    },
    birthday: {
        email: [
            {
                name: 'Default',
                text:
                    'Happy birthday, {first_name}! 🎉 Everyone at {business} wishes you an amazing day. ' +
                    'Come celebrate with us!',
            },
            {
                name: 'Short',
                text: 'Happy birthday, {first_name}! 🎉 From all of us at {business}.',
            },
        ],
        sms: [
            { name: 'Default', text: 'Happy birthday, {first_name}! 🎉 — {business}' },
        ],
    },
};
